import { headerManager } from "@/lib/requests";
import {
    AuthStatus,
    useAuthenticationManager,
} from "@/stores/authentication-store";
import { ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import {
    Organization,
    useOrganizationsManager,
} from "@/stores/organizations-store";
import { debounce } from "lodash";
import { defineStore, storeToRefs } from "pinia";

export const useOrganizationStore = defineStore("organization", () => {
    const route = useRoute();
    const router = useRouter();
    const { authStatus } = useAuthenticationManager();
    const { isLoading: isLoadingOrganizations, organizations } =
        useOrganizationsManager();

    const showSelectOrganization = ref(false);
    const organization = ref<Organization>();

    async function selectOrganization() {
        if (!route.meta.requiresOrg) {
            organization.value = undefined;
            return;
        }

        const orgNameParam = route.params.orgName as string;

        if (organization.value && organization.value.name === orgNameParam) {
            return;
        }

        if (!organizations.value) return;

        // Automatically select org if only one
        if (organizations.value.length === 1) {
            organization.value = organizations.value[0];
        } else {
            organization.value = organizations.value.find(
                (org) => org.name === orgNameParam
            );
        }
        if (!organization.value) {
            showSelectOrganization.value = true;
            return;
        }

        // When orgName is undefined while an organization is selected, update
        // the url to include the selected organization. (this can happen when
        // there is only one organization, in which case the user won't be
        // shown the organization picker where clicking a stek updates the
        // url.)
        if (!orgNameParam) {
            router.replace({
                name: "organizationPage",
                params: {
                    orgName: organization.value.name,
                },
            });
        }

        showSelectOrganization.value = false;
    }

    const debouncedSelectOrganization = debounce(selectOrganization, 100);

    // Set the header for the WebComponents requests lib.
    watch(
        () => organization.value?.identity,
        (newIdentity) => {
            if (newIdentity) {
                headerManager.updateHeaders({
                    organization: newIdentity,
                });
            }
        },
        { immediate: true }
    );

    watch(
        authStatus,
        async (newAuthStatus) => {
            if (newAuthStatus !== AuthStatus.SIGNED_IN) {
                organization.value = undefined;
            } else {
                await debouncedSelectOrganization();
            }
        },
        { immediate: true }
    );

    watch(
        () => route.params.orgName,
        (newOrgName, oldOrgName) => {
            if (newOrgName !== oldOrgName) {
                debouncedSelectOrganization();
            }
        },
        { immediate: true }
    );

    watch(
        () => route.meta.requiresOrg,
        (newRequiresOrg, oldRequiresOrg) => {
            if (!newRequiresOrg) {
                organization.value = undefined;
            } else if (newRequiresOrg !== oldRequiresOrg) {
                debouncedSelectOrganization();
            }
        },
        { immediate: true }
    );

    watch(
        organizations,
        (newOrgs) => {
            if (!newOrgs?.length) {
                organization.value = undefined;
                return;
            }
            debouncedSelectOrganization();
        },
        { deep: true, immediate: true }
    );

    return {
        showSelectOrganization,
        isLoadingOrganizations,
        organization,
    };
});

export const useOrganizationManager = () => storeToRefs(useOrganizationStore());
